/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, TableInfoSpec, TargetSpec } from "../../../shared/contracts";
import { TargetType } from "../../core/enums";
import { type IPhysicalData, ItemData } from "../../core/item-data";
import { resolvePhysics } from "../../core/material";
import { Vertex3D } from "../../math/vertex3d";

export class HitTargetData extends ItemData implements IPhysicalData {
  public dropSpeed: number = 0.5;
  public raiseDelay: number = 100;
  public elasticity: number = 0.35;
  public elasticityFalloff: number = 0.5;
  public friction: number = 0.2;
  public isCollidable: boolean = true;
  public isDropped: boolean = false;
  public isVisible: boolean = true;
  /** The engine always builds the hit shape in code, so this flag is only kept for the hit shape builder. */
  public legacy: boolean = false;
  /** The spec values are final, so the material does not overwrite them. */
  public overwritePhysics: boolean = true;
  /** Degrees. */
  public rotZ: number = 0;
  public scatter: number = 5;
  public szMaterial?: string;
  public szPhysicsMaterial?: string;
  public targetType: number = TargetType.DropTargetSimple;
  public threshold: number = 2.0;
  public useHitEvent: boolean = true;
  public position: Vertex3D = new Vertex3D();
  public vSize: Vertex3D = new Vertex3D(32, 32, 32);

  public constructor(itemName: string) {
    super(itemName);
  }

  /**
   * Builds the data from a table spec.
   * Physics values that the spec leaves out come from the material, then from the table.
   */
  public static fromSpec(
    spec: TargetSpec,
    info: TableInfoSpec,
    materials?: ReadonlyMap<string, MaterialSpec>,
  ): HitTargetData {
    const data = new HitTargetData(spec.name);
    const phys = resolvePhysics(spec, info, materials);
    data.position = new Vertex3D(spec.position.x, spec.position.y, spec.position.z);
    data.vSize = spec.vSize
      ? new Vertex3D(spec.vSize.x, spec.vSize.y, spec.vSize.z)
      : new Vertex3D(32, 32, 32);
    data.rotZ = spec.rotZ ?? 0;
    data.targetType = spec.targetType;
    data.isDropped = spec.isDropped ?? false;
    data.dropSpeed = spec.dropSpeed ?? data.dropSpeed;
    data.raiseDelay = spec.raiseDelay ?? data.raiseDelay;
    data.isCollidable = spec.isCollidable ?? true;
    data.isVisible = spec.isVisible ?? true;
    data.szMaterial = spec.szMaterial;
    data.szPhysicsMaterial = spec.szMaterial;
    data.elasticity = phys.elasticity;
    data.elasticityFalloff = phys.elasticityFalloff;
    data.friction = phys.friction;
    data.scatter = phys.scatter;
    data.threshold = phys.threshold ?? data.threshold;
    return data;
  }

  public isDropTarget(): boolean {
    return (
      this.targetType === TargetType.DropTargetBeveled ||
      this.targetType === TargetType.DropTargetFlatSimple ||
      this.targetType === TargetType.DropTargetSimple
    );
  }
}
